import axios from '@providers/axiosProvider';
import axios_ from 'axios';
import * as actions from './actions';
import {
  APIApproveConstructionRequest,
  APICreateConstructionReport,
  APIGetConstructionGuestRequest,
  APIGetConstructionInfoResponse,
  APIGetConstructionResponse,
  APIGetConstructionsListParams,
  APIGetConstructionsListResponse,
  Construction,
  ConstructionReportSubmitData,
  ConstructionsListData,
  ConstructionsListDisplayStatus,
  ConstructionInfoData,
  APIConstructionsCommentsResponse,
  ConstructionCommentsData,
  APISubmitConstructionRequest,
} from './types';
import { LIST_SIZE_PER_PAGE } from '@utils/constants';

/**
 *  @summary 工事一覧取得
 */
export const getConstructionsListAsync =
  (params: APIGetConstructionsListParams) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    const reqParams: APIGetConstructionsListParams = {
      ...params,
      size_per_page: LIST_SIZE_PER_PAGE,
    };

    await axios
      .get<APIGetConstructionsListResponse>(`/constructions`, {
        params: reqParams,
      })
      .then((response) => {
        const v = response.data.result;
        const constructionsList: ConstructionsListData[] = v.data.map((data) => ({
          construction_id: data.construction_id,
          property_id: data.property_id,
          property_name: data.property_name,
          status_id: data.status_id,
          status_name: data.status_name,
          start_date: data.start_date,
          title: data.title,
          reporter_id: data.reporter_id,
          reporter_name: data.reporter_name,
          latest_comment: data.latest_comment,
          update_datetime: data.update_datetime,
          update_user_name: data.update_user_name,
        }));

        const constructionsListDisplayStatus: ConstructionsListDisplayStatus = {
          num_per_page: v.display_params.num_per_page,
          current_page: v.display_params.current_page,
          total_page: v.display_params.total_page,
          total: v.display_params.total,
          sort: v.display_params.sort,
        };

        dispatch(actions.setConstructionsList(constructionsList));
        dispatch(actions.setConstructionsListDisplayStatus(constructionsListDisplayStatus));
      })
      .catch(() => {
        dispatch(actions.setConstructionsList([]));
        dispatch(actions.setConstructionsListDisplayStatus(null));
      });
    dispatch(actions.setIsLoading(false));
  };

/**
 *  @summary 工事一覧取得
 */
export const getConstructionAsync = (constructionId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<APIGetConstructionResponse>(`/constructions/${constructionId}`)
    .then((response) => {
      const v = response.data.result;

      const construction: Construction = {
        constructionId: v.construction_id,
        title: v.title,
        propertyId: v.property_id,
        propertyName: v.property_name,
        operators: v.operators.map((o) => ({
          companyId: o.company_id,
          companyName: o.company_name,
          userId: o.user_id,
          userName: o.user_name,
          emailAddress: o.email_address,
          isRepresentative: o.is_representative,
        })),
        workers: v.workers.map((w) => ({
          companyId: w.company_id,
          companyName: w.company_name,
          userId: w.user_id,
          userName: w.user_name,
          emailAddress: w.email_address,
          isRepresentative: w.is_representative,
        })),
        contractors: v.contractors.map((c) => ({
          companyId: c.company_id,
          companyName: c.company_name,
          userId: c.user_id,
          userName: c.user_name,
        })),
        contacts: v.contacts.map((co) => ({
          companyId: co.company_id,
          companyName: co.company_name,
          userId: co.user_id,
          userName: co.user_name,
        })),
        statusId: v.status_id,
        statusName: v.status_name,
        remarks: v.remarks,
        description: v.description,
        reporterId: v.reporter_id,
        reporterName: v.reporter_name,
        uploadDatetime: v.upload_datetime,
        files: v.reports.map((f) => ({
          fileName: f.file_name,
          memo: f.memo,
          fileUrl: f.file_url,
        })),
        reports: v.reports.map((r) => ({
          fileName: r.file_name,
          memo: r.memo,
          fileUrl: r.file_url,
          fileSize: r.file_size,
        })),
        updateDatetime: v.update_datetime,
        updateUserName: v.update_user_name,
      };

      dispatch(actions.setConstruction(construction));
    })
    .catch(() => {
      dispatch(actions.setConstruction(null));
    });

  dispatch(actions.setIsLoading(false));
};

/**
 * @summary 工事報告書登録・編集
 * @param {number} constructionId  工事ID
 * @param {ConstructionReportSubmitData} data
 * @returns {Promise<boolean>} 登録成否
 */
export const createConstructionReportAsync =
  (constructionId: number, data: ConstructionReportSubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const submitData: APICreateConstructionReport = {
      description: data.description,
      reports: data.reports.map((r) => ({
        file_name: r.fileName,
        file_content: r.fileUrl,
        is_delete_file: r.deleteFlag,
        memo: '',
      })),
      files: [],
    };

    const requestData: APICreateConstructionReport = submitData;

    return await axios
      .patch(`/constructions/${constructionId}`, requestData)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 工事報告書提出
 * @param {number} constructionId  工事ID
 * @param {params: { comment: string; }} data
 * @returns {Promise<boolean>} 登録成否
 */
export const submitConstructionAsync =
  (constructionId: number, reqParams: APISubmitConstructionRequest) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .patch(`/constructions/${constructionId}/submit`, reqParams)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 工事承認・差し戻し
 * @param {number} constructionId  工事ID
 * @param {APIApproveInspectionRequest} approveRequest  {承認/差し戻し, コメント}
 * @returns {Promise<boolean>} 登録成否
 */
export const approveConstructionAsync =
  (constructionId: number, approveRequest: APIApproveConstructionRequest) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .patch(`/constructions/${constructionId}/approve`, approveRequest)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

export const getConstructionGuestAsync =
  (constructionId: number, token: string) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));
    const reqParams: APIGetConstructionGuestRequest = { token };

    const guestMFAEnabled = process.env.REACT_APP_USE_GUESTMFA === '1';
    const api = guestMFAEnabled ? axios : axios_;
    const url = guestMFAEnabled ? '/guest/constructions' : '/api/guest/constructions';

    await api
      .get<APIGetConstructionResponse>(`${url}/${constructionId}`, {
        params: reqParams,
      })
      .then((response) => {
        const v = response.data.result;

        const construction: Construction = {
          constructionId: v.construction_id,
          title: v.title,
          propertyId: v.property_id,
          propertyName: v.property_name,
          operators: v.operators.map((o) => ({
            companyId: o.company_id,
            companyName: o.company_name,
            userId: o.user_id,
            userName: o.user_name,
            emailAddress: o.email_address,
            isRepresentative: o.is_representative,
          })),
          workers: v.workers.map((w) => ({
            companyId: w.company_id,
            companyName: w.company_name,
            userId: w.user_id,
            userName: w.user_name,
            emailAddress: w.email_address,
            isRepresentative: w.is_representative,
          })),
          contractors: v.contractors.map((c) => ({
            companyId: c.company_id,
            companyName: c.company_name,
            userId: c.user_id,
            userName: c.user_name,
          })),
          contacts: v.contacts.map((co) => ({
            companyId: co.company_id,
            companyName: co.company_name,
            userId: co.user_id,
            userName: co.user_name,
          })),
          statusId: v.status_id,
          statusName: v.status_name,
          remarks: v.remarks,
          description: v.description,
          reporterId: v.reporter_id,
          reporterName: v.reporter_name,
          uploadDatetime: v.upload_datetime,
          files: v.reports.map((f) => ({
            fileName: f.file_name,
            memo: f.memo,
            fileUrl: f.file_url,
          })),
          reports: v.reports.map((r) => ({
            fileName: r.file_name,
            memo: r.memo,
            fileUrl: r.file_url,
            fileSize: r.file_size,
          })),
          updateDatetime: v.update_datetime,
          updateUserName: v.update_user_name,
        };
        dispatch(actions.setConstruction(construction));
      })
      .catch((error) => {
        alert(error.response?.data.message ?? '');
        dispatch(actions.setConstruction(null));
        dispatch(actions.setIsInvalidToken(true));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 工事報告書登録・編集（ゲストモード）
 * @param {number} constructionId  工事ID
 * @param {string} token
 * @param {ConstructionReportSubmitData} data
 * @returns {Promise<boolean>} 登録成否
 */
export const createConstructionReportGuestAsync =
  (constructionId: number, token: string, data: ConstructionReportSubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APIGetConstructionGuestRequest = { token };

    const submitData: APICreateConstructionReport = {
      description: data.description,
      reports: data.reports.map((r) => ({
        file_name: r.fileName,
        file_content: r.fileUrl,
        is_delete_file: r.deleteFlag,
        memo: '',
      })),
      files: [],
    };

    const requestData: APICreateConstructionReport = submitData;

    return await axios_
      .patch(`/api/guest/constructions/${constructionId}`, requestData, { params: reqParams })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch((error) => {
        alert(error.response?.data.message ?? '');
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 工事報告書提出（ゲストモード）
 * @param {number} constructionId  工事ID
 * @param {params: { comment: string; }} data
 * @param {string} token
 * @returns {Promise<boolean>} 登録成否
 */
export const submitConstructionGuestAsync =
  (
    constructionId: number,
    params: {
      comment: string;
    },
    token: string,
  ) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APIGetConstructionGuestRequest = { token };

    return await axios_
      .patch(`/api/guest/constructions/${constructionId}/submit`, params, {
        params: reqParams,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch((error) => {
        alert(error.response?.data.message ?? '');
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/** @summary 工事-連絡事項情報取得 */
export const setConstructionInformationAsync =
  (constructionId: number) => async (dispatch: any) => {
    dispatch(actions.setIsLoading2(true));

    await axios
      .get<APIGetConstructionInfoResponse>(`/constructions/${constructionId}/information`)
      .then((response) => {
        const resultData = response.data.result;
        const constructionInfoData: ConstructionInfoData = {
          constructionId: resultData.construction_id,
          title: resultData.title,
          propertyId: resultData.property_id,
          propertyName: resultData.property_name,
          message: resultData.message,
          fileList: (resultData.file_list ?? []).map((f) => ({
            fileId: f.file_id,
            fileName: f.file_name,
            fileUrl: f.file_url,
            fileSize: f.file_size,
          })),
        };
        dispatch(actions.setConstructionInfo(constructionInfoData));
      })
      .catch(() => {
        dispatch(actions.setConstructionInfo(null));
      });
    dispatch(actions.setIsLoading2(false));
  };

/** @summary 工事-連絡事項情報取得 */
export const setConstructionInformationGuestAsync =
  (constructionId: number, token: string) => async (dispatch: any) => {
    dispatch(actions.setIsLoading2(true));
    const reqParams: APIGetConstructionGuestRequest = { token };

    const guestMFAEnabled = process.env.REACT_APP_USE_GUESTMFA === '1';
    const api = guestMFAEnabled ? axios : axios_;
    const url = guestMFAEnabled ? '/guest/constructions' : '/api/guest/constructions';

    await api
      .get<APIGetConstructionInfoResponse>(`${url}/${constructionId}/information`, {
        params: reqParams,
      })
      .then((response) => {
        const resultData = response.data.result;
        const constructionInfoData: ConstructionInfoData = {
          constructionId: resultData.construction_id,
          title: resultData.title,
          propertyId: resultData.property_id,
          propertyName: resultData.property_name,
          message: resultData.message,
          fileList: (resultData.file_list ?? []).map((f) => ({
            fileId: f.file_id,
            fileName: f.file_name,
            fileUrl: f.file_url,
            fileSize: f.file_size,
          })),
        };
        dispatch(actions.setConstructionInfo(constructionInfoData));
      })
      .catch(() => {
        dispatch(actions.setConstructionInfo(null));
      });
    dispatch(actions.setIsLoading2(false));
  };

/** @summary 工事コメント一覧取得 */
export const getConstructionsCommentsAsync = (constructionId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading2(true));

  await axios
    .get<APIConstructionsCommentsResponse>(`/constructions/${constructionId}/comments`)
    .then((response) => {
      const resultData = response.data.result;
      const constructionsCommentsData: ConstructionCommentsData = {
        commentList: resultData.comment_list.map((c) => ({
          commentId: c.comment_id,
          typeId: c.type_id,
          typeName: c.type_name,
          beforeStatusId: c.before_status_id,
          beforeStatusName: c.before_status_name,
          afterStatusId: c.after_status_id,
          afterStatusName: c.after_status_name,
          createUserId: c.create_user_id,
          createUserName: c.create_user_name,
          createDatetime: c.create_datetime,
          updateDatetime: c.update_datetime,
          comment: c.comment,
          imageList: c.image_list.map((i) => ({
            fileId: i.file_id,
            fileName: i.file_name,
            fileUrl: i.file_url,
            thumbnailFileUrl: i.thumbnail_file_url,
            fileSize: i.file_size,
            memo: i.memo,
          })),
          fileList: c.file_list.map((f) => ({
            fileId: f.file_id,
            fileName: f.file_name,
            fileUrl: f.file_url,
            fileSize: f.file_size,
          })),
        })),
      };
      dispatch(actions.setConstructionComments(constructionsCommentsData));
    })
    .catch(() => {
      dispatch(actions.setConstructionComments(null));
      return;
    });
  dispatch(actions.setIsLoading2(false));
};
